/* global NodeJS */
import { logger } from "./logger";
// import { performanceMonitor } from "./performance";
import { supabase } from "../lib/supabase";

interface AnalyticsEvent {
  event: string;
  category: string;
  properties?: Record<string, unknown>;
  timestamp?: number;
  userId?: string;
  sessionId?: string;
}

interface UserSession {
  sessionId: string;
  startTime: number;
  lastActivity: number;
  pageViews: number;
  events: AnalyticsEvent[];
  userId?: string;
  language: string;
  userAgent: string;
  screen: {
    width: number;
    height: number;
  };
  location?: {
    zipCode?: string;
    need?: string;
  };
}

class AnalyticsManager {
  private session: UserSession;
  private eventQueue: AnalyticsEvent[] = [];
  private isEnabled: boolean;
  private flushInterval: NodeJS.Timeout | null = null;
  constructor() {
    this.isEnabled = true; // Analytics enabled by default since we're hardcoding values
    this.session = this.initializeSession();

    if (this.isEnabled && typeof window !== "undefined") {
      this.setupEventListeners();
      this.startPeriodicFlush();
    }
  }

  private initializeSession(): UserSession {
    const sessionId = this.generateSessionId();
    return {
      sessionId,
      startTime: Date.now(),
      lastActivity: Date.now(),
      pageViews: 0,
      events: [],
      language: navigator.language,
      userAgent: navigator.userAgent,
      screen: {
        width: window.screen?.width || 0,
        height: window.screen?.height || 0,
      },
    };
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private setupEventListeners(): void {
    // Page visibility changes
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        this.track("page_hidden", "engagement");
      } else {
        this.track("page_visible", "engagement");
      }
    });

    // Page unload
    window.addEventListener("beforeunload", () => {
      this.track("session_end", "session", {
        duration: Date.now() - this.session.startTime,
        pageViews: this.session.pageViews,
        totalEvents: this.session.events.length,
      });
      this.flush();
    });

    // Error tracking
    window.addEventListener("error", (error) => {
      this.track("javascript_error", "error", {
        message: error.message,
        filename: error.filename,
        lineno: error.lineno,
        colno: error.colno,
        stack: error.error?.stack,
      });
    });

    // Unhandled promise rejections
    window.addEventListener("unhandledrejection", (event) => {
      this.track("unhandled_promise_rejection", "error", {
        reason: event.reason,
        promise: event.promise,
      });
    });

    // Mouse activity for engagement tracking
    let mouseMovements = 0;
    document.addEventListener("mousemove", () => {
      mouseMovements++;
      if (mouseMovements % 100 === 0) {
        // Track every 100 movements
        this.updateActivity();
      }
    });

    // Keyboard activity
    document.addEventListener("keydown", () => {
      this.updateActivity();
    });

    // Scroll depth tracking
    let maxScrollDepth = 0;
    window.addEventListener("scroll", () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.body.scrollHeight - window.innerHeight)) *
          100
      );
      if (scrollPercent > maxScrollDepth) {
        maxScrollDepth = scrollPercent;
        if (maxScrollDepth % 25 === 0) {
          // Track at 25%, 50%, 75%, 100%
          this.track("scroll_depth", "engagement", { depth: maxScrollDepth });
        }
      }
    });
  }

  private updateActivity(): void {
    this.session.lastActivity = Date.now();
  }

  private startPeriodicFlush(): void {
    this.flushInterval = setInterval(() => {
      this.flush();
    }, 30000); // Flush every 30 seconds
  }

  /**
   * Track an analytics event
   */ track(
    event: string,
    category: string,
    properties?: Record<string, unknown>
  ): void {
    if (!this.isEnabled) return;

    const analyticsEvent: AnalyticsEvent = {
      event,
      category,
      properties,
      timestamp: Date.now(),
      sessionId: this.session.sessionId,
      userId: this.session.userId,
    };

    this.eventQueue.push(analyticsEvent);
    this.session.events.push(analyticsEvent);
    this.updateActivity();
    logger.info(
      `Analytics event: ${event}`,
      category,
      {
        properties,
        sessionId: this.session.sessionId,
      }
    );

    // Auto-flush for critical events
    if (category === "error" || event === "session_end") {
      this.flush();
    }
  }

  /**
   * Track page view
   */
  trackPageView(path: string, title?: string): void {
    this.session.pageViews++;
    this.track("page_view", "navigation", {
      path,
      title,
      referrer: document.referrer,
      timestamp: Date.now(),
    });
  }

  /**
   * Track user interaction
   */ trackInteraction(
    element: string,
    action: string,
    properties?: Record<string, unknown>
  ): void {
    this.track("user_interaction", "engagement", {
      element,
      action,
      ...properties,
    });
  }

  /**
   * Track feature usage
   */ trackFeatureUsage(
    feature: string,
    action: string,
    properties?: Record<string, unknown>
  ): void {
    this.track("feature_usage", "features", {
      feature,
      action,
      ...properties,
    });
  }

  /**
   * Track conversion events
   */ trackConversion(
    type: string,
    value?: number,
    properties?: Record<string, unknown>
  ): void {
    this.track("conversion", "business", {
      type,
      value,
      ...properties,
    });
  }

  /**
   * Track performance metrics
   */ trackPerformance(
    metric: string,
    value: number,
    properties?: Record<string, unknown>
  ): void {
    this.track("performance_metric", "performance", {
      metric,
      value,
      ...properties,
    });
  }

  /**
   * Set user ID for tracking
   */
  setUserId(userId: string): void {
    this.session.userId = userId;
    this.track("user_identified", "user", { userId });
  }

  /**
   * Set user properties
   */
  setUserProperties(properties: Record<string, unknown>): void {
    Object.assign(this.session, properties);
    this.track("user_properties_updated", "user", properties);
  }

  /**
   * Track user location context
   */
  setLocationContext(zipCode?: string, need?: string): void {
    this.session.location = { zipCode, need };
    this.track("location_context_set", "context", { zipCode, need });
  }

  /**
   * Flush events to server
   */
  private async flush(): Promise<void> {
    if (this.eventQueue.length === 0) return;

    const events = [...this.eventQueue];
    this.eventQueue = [];

    try {
      // Store events in localStorage for development
      if (import.meta.env.DEV) {
        const existingEvents = JSON.parse(localStorage.getItem('rightnowaid_analytics_events') || '[]');
        localStorage.setItem('rightnowaid_analytics_events', JSON.stringify([...existingEvents, ...events]));
        logger.info(
          "Analytics events stored in localStorage",
          "analytics",
          { count: events.length }
        );
        return;
      }

      // In production, send to Supabase
      const { error } = await supabase
        .from('analytics_events')
        .insert(events.map(event => ({
          ...event,
          session_id: this.session.sessionId,
          user_id: this.session.userId,
          timestamp: new Date().toISOString()
        })));

      if (error) {
        throw error;
      }

      logger.info(
        "Analytics events flushed",
        "analytics",
        { count: events.length }
      );
    } catch (error) {
      logger.error(
        "Failed to flush analytics events",
        "analytics",
        { error: error instanceof Error ? error.message : String(error) }
      );
      // Re-add events to queue for retry
      this.eventQueue.unshift(...events);
    }
  }

  /**
   * Get session summary
   */
  getSessionSummary(): UserSession {
    return {
      ...this.session,
      events: [...this.session.events], // Return copy
    };
  }

  /**
   * Enable/disable analytics
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    if (enabled && !this.flushInterval) {
      this.startPeriodicFlush();
    } else if (!enabled && this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }
    this.flush(); // Final flush
  }
}

// Create global instance
export const analytics = new AnalyticsManager();

// React hook for analytics
export const useAnalytics = () => {
  const track = (
    event: string,
    category: string,
    properties?: Record<string, unknown>
  ) => analytics.track(event, category, properties);

  const trackPageView = (path: string, title?: string) =>
    analytics.trackPageView(path, title);
  const trackInteraction = (
    element: string,
    action: string,
    properties?: Record<string, unknown>
  ) => analytics.trackInteraction(element, action, properties);
  const trackFeatureUsage = (
    feature: string,
    action: string,
    properties?: Record<string, unknown>
  ) => analytics.trackFeatureUsage(feature, action, properties);
  const trackConversion = (
    type: string,
    value?: number,
    properties?: Record<string, unknown>
  ) => analytics.trackConversion(type, value, properties);
  const trackPerformance = (
    metric: string,
    value: number,
    properties?: Record<string, unknown>
  ) => analytics.trackPerformance(metric, value, properties);

  return {
    track,
    trackPageView,
    trackInteraction,
    trackFeatureUsage,
    trackConversion,
    trackPerformance,
    setUserId: analytics.setUserId.bind(analytics),
    setUserProperties: analytics.setUserProperties.bind(analytics),
    setLocationContext: analytics.setLocationContext.bind(analytics),
    getSessionSummary: analytics.getSessionSummary.bind(analytics),
  };
};

// Auto-initialize performance tracking integration
if (typeof window !== "undefined") {
  // Track Core Web Vitals automatically
  // TODO: Implement proper performance integration
  // performanceMonitor.onWebVital((metric: unknown) => {
  //   analytics.trackPerformance(metric.name, metric.value, {
  //     rating: metric.rating,
  //     navigationType: metric.navigationType,
  //   });
  // });
}
